import React from 'react';
import { Plus } from 'lucide-react';
import { formatDuration, formatTime } from '../utils/formatters';

export function VisualTimeline({ entries, currentTimer, currentTime, getProjectById, onGapClick }) {
  const allEntries = currentTimer ? [currentTimer, ...entries] : entries;

  if (allEntries.length === 0) {
    return null;
  }

  // Find time range
  const allStartTimes = allEntries.map(e => e.startTime);
  const allEndTimes = allEntries.map(e => e.endTime || currentTime);

  const minTime = Math.min(...allStartTimes);
  const maxTime = Math.max(...allEndTimes);

  // Expand by 1 hour on each side, round to hour
  const startHour = new Date(minTime);
  startHour.setMinutes(0, 0, 0);
  startHour.setHours(startHour.getHours() - 1);

  const endHour = new Date(maxTime);
  endHour.setMinutes(0, 0, 0);
  endHour.setHours(endHour.getHours() + 2);

  const timelineStart = startHour.getTime();
  const timelineEnd = endHour.getTime();
  const totalDuration = timelineEnd - timelineStart;

  // Generate hour markers
  const hours = [];
  let hourMarker = new Date(timelineStart);
  while (hourMarker.getTime() <= timelineEnd) {
    hours.push(new Date(hourMarker));
    hourMarker.setHours(hourMarker.getHours() + 1);
  }

  const getPosition = (timestamp) => {
    return ((timestamp - timelineStart) / totalDuration) * 100;
  };

  const getHeight = (start, end) => {
    return ((end - start) / totalDuration) * 100;
  };

  // Sort entries by start time and find gaps
  const sortedEntries = [...allEntries].sort((a, b) => a.startTime - b.startTime);
  const gaps = [];

  // Gap before first entry
  if (sortedEntries.length > 0 && sortedEntries[0].startTime > timelineStart) {
    gaps.push({ start: timelineStart, end: sortedEntries[0].startTime });
  }

  // Gaps between entries
  for (let i = 0; i < sortedEntries.length - 1; i++) {
    const currentEnd = sortedEntries[i].endTime || currentTime;
    const nextStart = sortedEntries[i + 1].startTime;
    if (nextStart > currentEnd) {
      gaps.push({ start: currentEnd, end: nextStart });
    }
  }

  // Gap after last entry (only if not running)
  const lastEntry = sortedEntries[sortedEntries.length - 1];
  if (lastEntry && lastEntry.endTime && lastEntry.endTime < timelineEnd) {
    gaps.push({ start: lastEntry.endTime, end: timelineEnd });
  }

  const minGapMinutes = 5;

  return (
    <div className="mb-6 bg-gray-50 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-600">Visual Timeline</h3>
        <div className="flex flex-wrap gap-3">
          {[...new Set(allEntries.map(e => e.projectId))].map(projectId => {
            const project = getProjectById(projectId);
            return (
              <div key={projectId} className="flex items-center gap-1.5 text-xs">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: project.color }} />
                <span className="text-gray-600">{project.name}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex">
        {/* Hour labels */}
        <div className="relative w-16 mr-2" style={{ height: '400px' }}>
          {hours.map((hour, idx) => (
            <div
              key={idx}
              className="absolute right-0 text-xs text-gray-500 transform -translate-y-1/2 pr-2"
              style={{ top: `${getPosition(hour.getTime())}%` }}
            >
              {hour.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
            </div>
          ))}
        </div>

        {/* Timeline bar */}
        <div className="relative flex-1 bg-gray-200 rounded-lg overflow-hidden" style={{ height: '400px' }}>
          {/* Hour grid lines */}
          {hours.map((hour, idx) => (
            <div
              key={idx}
              className="absolute left-0 right-0 h-px bg-gray-300"
              style={{ top: `${getPosition(hour.getTime())}%` }}
            />
          ))}

          {/* Clickable gaps */}
          {gaps.map((gap, idx) => {
            const gapMinutes = (gap.end - gap.start) / (1000 * 60);
            if (gapMinutes < minGapMinutes) return null;

            return (
              <div
                key={`gap-${idx}`}
                className="absolute left-1 right-1 bg-gray-100 hover:bg-blue-100 border-2 border-dashed border-gray-300 hover:border-blue-400 rounded cursor-pointer transition-colors flex items-center justify-center group"
                style={{
                  top: `${getPosition(gap.start)}%`,
                  height: `${Math.max(getHeight(gap.start, gap.end), 2)}%`,
                }}
                onClick={() => onGapClick(gap.start, gap.end)}
                title={`Gap: ${formatTime(gap.start)} - ${formatTime(gap.end)} (click to add entry)`}
              >
                <div className="text-gray-400 group-hover:text-blue-500 transition-colors">
                  <Plus size={16} />
                </div>
              </div>
            );
          })}

          {/* Time entries */}
          {allEntries.map((entry) => {
            const project = getProjectById(entry.projectId);
            const entryEnd = entry.endTime || currentTime;
            const isRunning = !entry.endTime;
            const heightPercent = getHeight(entry.startTime, entryEnd);

            return (
              <div
                key={entry.id}
                className={`absolute left-1 right-1 rounded px-2 py-1 overflow-hidden flex flex-col ${isRunning ? 'animate-pulse' : ''}`}
                style={{
                  top: `${getPosition(entry.startTime)}%`,
                  height: `${Math.max(heightPercent, 3)}%`,
                  backgroundColor: project.color,
                }}
                title={`${entry.description || 'No description'} - ${project.name} (${formatTime(entry.startTime)} - ${isRunning ? 'Running' : formatTime(entry.endTime)})`}
              >
                <div className="text-white text-xs font-medium truncate">
                  {entry.description || <span className="italic opacity-80">No description</span>}
                </div>
                <div className="text-white/90 text-xs font-medium">
                  {project.name}
                </div>
                {heightPercent > 8 && (
                  <div className="text-white/70 text-xs mt-auto">
                    {formatTime(entry.startTime)} - {isRunning ? 'Now' : formatTime(entry.endTime)}
                  </div>
                )}
                <div className="text-white/80 text-xs font-semibold">
                  {formatDuration(entryEnd - entry.startTime)}
                </div>
              </div>
            );
          })}

          {/* Current time indicator */}
          <div
            className="absolute left-0 right-0 h-0.5 bg-red-500 z-10"
            style={{ top: `${getPosition(currentTime)}%` }}
          >
            <div className="absolute top-1/2 -left-1 transform -translate-y-1/2 w-2 h-2 bg-red-500 rounded-full" />
            <div className="absolute top-1/2 left-3 transform -translate-y-1/2 text-xs text-red-500 font-medium bg-white px-1 rounded">
              Now
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
